
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../../components/feature/Navigation';
import Button from '../../../components/base/Button';
import LanguageSelector from '../../../components/common/LanguageSelector';
import { useTimer } from '../../../hooks/useTimer';
import { formatTimerTime } from '../../../utils/timeFormat';
import { getSEOConfig, updateSEOTags } from '../../../i18n/utils/seo';
import type { SupportedLanguage } from '../../../i18n/utils/language';

export default function PresetTimer() {
  const { minutes } = useParams<{ minutes: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['timer', 'common']);
  
  // 添加缺失的状态定义
  const [editingHours, setEditingHours] = useState(false);
  const [editingMinutes, setEditingMinutes] = useState(false);
  const [tempHours, setTempHours] = useState('00');
  const [tempMinutesValue, setTempMinutesValue] = useState('00');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  
  // 解析URL参数，支持多种格式：/timer/5、/timer/5-minute-timer、/timer/10-seconds-timer、/timer/24-hour-timer
  const getMinutesFromParam = (param: string | undefined): number => {
    if (!param) return 5;

    // 检查是否是小时级格式 XX-hour-timer
    if (param.includes('-hour-timer')) {
      const hoursStr = param.replace('-hour-timer', '');
      const hours = parseInt(hoursStr, 10) || 0;
      return hours * 60; // 转换为分钟
    }

    // 检查是否是秒级格式 XX-seconds-timer
    if (param.includes('-seconds-timer')) {
      const secondsStr = param.replace('-seconds-timer', '');
      const seconds = parseInt(secondsStr, 10) || 0;
      return seconds / 60; // 转换为分钟（可能是小数）
    }

    // 检查是否是新格式 XX-minute-timer
    if (param.includes('-minute-timer')) {
      const minuteStr = param.replace('-minute-timer', '');
      return parseInt(minuteStr, 10) || 5;
    }

    // 原格式直接解析
    return parseInt(param, 10) || 5;
  };

  const timerMinutes = getMinutesFromParam(minutes);

  const initialTime = timerMinutes * 60 * 1000;

  // 判断是否为秒级计时器
  const isSecondsTimer = timerMinutes < 1;
  const timerSeconds = Math.round(timerMinutes * 60);

  // 判断是否为小时级计时器（只有URL包含-hour-timer才算小时级）
  const isHoursTimer = minutes?.includes('-hour-timer') || false;
  const timerHours = Math.round(timerMinutes / 60);

  // 判断是否有use case内容
  const hasUseCases = [1, 3, 5, 10, 15, 30].includes(timerMinutes);

  // 随机排序用户评价
  const [randomizedReviews, setRandomizedReviews] = useState<Array<{
    id: number;
    avatar: string;
    name: string;
    rating: number;
    review: string;
  }>>([]);

  useEffect(() => {
    // 根据计时器时长创建对应的用户评价数据
    let userReviews: Array<{
      id: number;
      avatar: string;
      name: string;
      rating: number;
      review: string;
    }> = [];

    switch (timerMinutes) {
      case 1:
        userReviews = [
          {
            id: 1,
            avatar: "/images/avatars/Alex Office.jpg",
            name: t('user.officeWorkerAlex'),
            rating: 5,
            review: t('reviews.officeWorkerAlex')
          },
          {
            id: 2,
            avatar: "/images/avatars/Sarah Dev.jpg",
            name: t('user.developerSarah'),
            rating: 5,
            review: t('reviews.developerSarah')
          },
          {
            id: 3,
            avatar: "/images/avatars/Mike Design.jpg",
            name: t('user.designerMike'),
            rating: 4,
            review: t('reviews.designerMike')
          }
        ];
        break;
      case 3:
        userReviews = [
          {
            id: 1,
            avatar: "/images/avatars/Emily Tea.jpg",
            name: t('user.teaLoverEmily'),
            rating: 5,
            review: t('reviews.teaLoverEmily')
          },
          {
            id: 2,
            avatar: "/images/avatars/John Yoga.jpg",
            name: t('user.yogatrainerJohn'),
            rating: 5,
            review: t('reviews.yogatrainerJohn')
          },
          {
            id: 3,
            avatar: "/images/avatars/Lisa Mom.jpg",
            name: t('user.busyMomLisa'),
            rating: 4,
            review: t('reviews.busyMomLisa')
          }
        ];
        break;
      case 5:
        userReviews = [
          {
            id: 1,
            avatar: "/images/avatars/Zen Master.jpg",
            name: t('user.meditatorZen'),
            rating: 5,
            review: t('reviews.meditatorZen')
          },
          {
            id: 2,
            avatar: "/images/avatars/Chef Tony.jpg",
            name: t('user.chefTony'),
            rating: 5,
            review: t('reviews.chefTony')
          },
          {
            id: 3,
            avatar: "/images/avatars/Mia Runner.jpg",
            name: t('user.runnerMia'),
            rating: 4,
            review: t('reviews.runnerMia')
          }
        ];
        break;
      case 10:
        userReviews = [
          {
            id: 1,
            avatar: "/images/avatars/David Student.jpg",
            name: t('user.studentDavid'),
            rating: 5,
            review: t('reviews.studentDavid')
          },
          {
            id: 2,
            avatar: "/images/avatars/Rachel Manager.jpg",
            name: t('user.managerRachel'),
            rating: 5,
            review: t('reviews.managerRachel')
          },
          {
            id: 3,
            avatar: "/images/avatars/James Writer.jpg",
            name: t('user.writerJames'),
            rating: 4,
            review: t('reviews.writerJames')
          }
        ];
        break;
      case 15:
        userReviews = [
          {
            id: 1,
            avatar: "/images/avatars/Amy Consultant.jpg",
            name: t('user.consultantAmy'),
            rating: 5,
            review: t('reviews.consultantAmy')
          },
          {
            id: 2,
            avatar: "/images/avatars/Ben Teacher.jpg",
            name: t('user.teacherBen'),
            rating: 5,
            review: t('reviews.teacherBen')
          },
          {
            id: 3,
            avatar: "/images/avatars/Sophie Freelancer.jpg",
            name: t('user.freelancerSophie'),
            rating: 4,
            review: t('reviews.freelancerSophie')
          }
        ];
        break;
      case 30:
        userReviews = [
          {
            id: 1,
            avatar: "/images/avatars/Robert Executive.jpg",
            name: t('user.executiveRobert'),
            rating: 5,
            review: t('reviews.executiveRobert')
          },
          {
            id: 2,
            avatar: "/images/avatars/Maya Fitness.jpg",
            name: t('user.fitnessCoachMaya'),
            rating: 5,
            review: t('reviews.fitnessCoachMaya')
          },
          {
            id: 3,
            avatar: "/images/avatars/Olivia Artist.jpg",
            name: t('user.artistOlivia'),
            rating: 4,
            review: t('reviews.artistOlivia')
          }
        ];
        break;
      default:
        // 对于其他时长，使用通用评价
        userReviews = [
          {
            id: 1,
            avatar: "/images/avatars/Manager Zhang.jpg",
            name: t('user.managerZhang'),
            rating: 5,
            review: t('reviews.managerZhang')
          },
          {
            id: 2,
            avatar: "/images/avatars/Mrs. Li.jpg",
            name: t('user.mrsLi'),
            rating: 5,
            review: t('reviews.mrsLi')
          },
          {
            id: 3,
            avatar: "/images/avatars/Programmer Wang.jpg",
            name: t('user.programmerWang'),
            rating: 4,
            review: t('reviews.programmerWang')
          }
        ];
    }

    // 随机排序用户评价
    const shuffled = [...userReviews].sort(() => Math.random() - 0.5);
    setRandomizedReviews(shuffled);
  }, [t, i18n.language, timerMinutes]);

  const { time, isRunning, canStart, start, pause, reset, setTime } = useTimer({
    initialTime,
    t,
    language: i18n.language,
    soundEnabled,
    onComplete: () => {
      // Show completion modal
      setShowCompletionModal(true);
      
      // Desktop notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(t('common:message.timerComplete'), {
          body: t('common:message.timerCompleteDesc'),
          icon: '/favicon.png'
        });
      }

      // Sound alert
      if (soundEnabled) {
        const playAlarm = () => {
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

          const createBeep = (frequency: number, duration: number, delay: number) => {
            setTimeout(() => {
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();

              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);

              oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
              oscillator.type = 'sine';

              gainNode.gain.setValueAtTime(0, audioContext.currentTime);
              gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
              gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + duration);
            }, delay);
          };

          for (let i = 0; i < 10; i++) {
            const freq = i % 2 === 0 ? 800 : 600;
            createBeep(freq, 0.4, i * 500);
          }
        };

        try {
          playAlarm();
        } catch {
          const audio = new Audio(
            'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwgBS2Gy/LYeSkELYTO8tuGMwgeZ7bv6qBVEQpKoeDwt2wcAT6Mz/LWgiMFJH3J8daWRAwUYrPm7K1YEwlHmN/wul0fBSuTzvLWfSMFKX3K8daWRAweY7nr6qJZEQpInd7wuVAiAj2TzfLWfyUF'
          );
          audio.volume = 0.8;
          const playCount = 5;
          let played = 0;
          const playNext = () => {
            if (played < playCount) {
              audio.currentTime = 0;
              audio
                .play()
                .then(() => {
                  played++;
                  setTimeout(playNext, 800);
                })
                .catch(() => {
                  // ignore playback errors
                });
            }
          };
          playNext();
        }
      }
    }
  });

  // Ensure timer resets when preset changes
  useEffect(() => {
    setTime(initialTime);
  }, [initialTime, setTime]);

  // Update SEO on route change
  useEffect(() => {
    if (minutes) {
      const currentLang = i18n.language as SupportedLanguage;
      const seoConfig = getSEOConfig('timerMinutes', currentLang, timerMinutes);
      updateSEOTags(seoConfig, currentLang);
    }
  }, [minutes, i18n.language, timerMinutes]);

  // Extract display values
  const totalMs = Math.abs(time);
  const hours = Math.floor(totalMs / 3600000);
  const minutesDisplay = Math.floor((totalMs % 3600000) / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);

  const handleHoursClick = () => {
    if (!isRunning) {
      setEditingHours(true);
      setTempHours(hours.toString().padStart(2, '0'));
    }
  };

  const handleMinutesClick = () => {
    if (!isRunning) {
      setEditingMinutes(true);
      setTempMinutesValue(minutesDisplay.toString().padStart(2, '0'));
    }
  };

  const handleHoursChange = (value: string) => {
    const num = parseInt(value, 10) || 0;
    const clamped = Math.min(Math.max(num, 0), 23);
    setTempHours(clamped.toString().padStart(2, '0'));
  };

  const handleMinutesChange = (value: string) => {
    const num = parseInt(value, 10) || 0;
    const clamped = Math.min(Math.max(num, 0), 59);
    setTempMinutesValue(clamped.toString().padStart(2, '0'));
  };

  const handleHoursSubmit = () => {
    const newHours = parseInt(tempHours, 10) || 0;
    const newTime = (newHours * 3600 + minutesDisplay * 60 + seconds) * 1000;
    setTime(newTime);
    setEditingHours(false);
  };

  const handleMinutesSubmit = () => {
    const newMinutes = parseInt(tempMinutesValue, 10) || 0;
    const newTime = (hours * 3600 + newMinutes * 60 + seconds) * 1000;
    setTime(newTime);
    setEditingMinutes(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent, isHours: boolean) => {
    if (e.key === 'Enter') {
      isHours ? handleHoursSubmit() : handleMinutesSubmit();
    } else if (e.key === 'Escape') {
      setEditingHours(false);
      setEditingMinutes(false);
    }
  };

  const addTime = (min: number) => {
    setTime(time + min * 60 * 1000);
  };

  const addSeconds = (sec: number) => {
    setTime(time + sec * 1000);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleFullscreenKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsFullscreen(false);
    }
  };

  const handleCompletionModalClose = () => {
    setShowCompletionModal(false);
  };

  // Fullscreen view
  if (isFullscreen) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
        onClick={toggleFullscreen}
        onKeyDown={handleFullscreenKeyPress}
        tabIndex={0}
      >
        <div className="text-center" onClick={e => e.stopPropagation()}>
          <button
            onClick={toggleFullscreen}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>

          <div className="flex items-center justify-center space-x-4 mb-12">
            {(hours > 0 || editingHours) && (
              <>
                <div className="flex flex-col items-center">
                  {editingHours ? (
                    <input
                      type="number"
                      value={tempHours}
                      onChange={e => handleHoursChange(e.target.value)}
                      onBlur={handleHoursSubmit}
                      onKeyDown={e => handleKeyPress(e, true)}
                      className="text-8xl md:text-9xl font-mono font-[700] text-white bg-transparent border-b-4 border-blue-300 text-center w-40 md:w-48 focus:outline-none focus:border-blue-500"
                      autoFocus
                      min="0"
                      max="23"
                    />
                  ) : (
                    <button
                      onClick={handleHoursClick}
                      className="text-8xl md:text-9xl font-mono font-[700] text-white hover:text-blue-300 cursor-pointer transition-colors w-40 md:w-48 text-center"
                      disabled={isRunning}
                    >
                      {hours.toString().padStart(2, '0')}
                    </button>
                  )}
                  <span className="text-lg text-gray-300 mt-4">{t('common:label.hours')}</span>
                </div>

                <div className="flex items-center justify-center h-48">
                  <span className="text-8xl md:text-9xl font-mono font-[700] text-gray-400">:</span>
                </div>
              </>
            )}

            <div className="flex flex-col items-center">
              {editingMinutes ? (
                <input
                  type="number"
                  value={tempMinutesValue}
                  onChange={e => handleMinutesChange(e.target.value)}
                  onBlur={handleMinutesSubmit}
                  onKeyDown={e => handleKeyPress(e, false)}
                  className="text-8xl md:text-9xl font-mono font-[700] text-white bg-transparent border-b-4 border-blue-300 text-center w-40 md:w-48 focus:outline-none focus:border-blue-500"
                  autoFocus
                  min="0"
                  max="59"
                />
              ) : (
                <button
                  onClick={handleMinutesClick}
                  className="text-8xl md:text-9xl font-mono font-[700] text-white hover:text-blue-300 cursor-pointer transition-colors w-40 md:w-48 text-center"
                  disabled={isRunning}
                >
                  {minutesDisplay.toString().padStart(2, '0')}
                </button>
              )}
              <span className="text-lg text-gray-300 mt-4">{t('common:label.minutes')}</span>
            </div>

            <div className="flex items-center justify-center h-48">
              <span className="text-8xl md:text-9xl font-mono font-[700] text-gray-400">:</span>
            </div>

            <div className="flex flex-col items-center">
              <span className="text-8xl md:text-9xl font-mono font-[700] text-gray-300 w-40 md:w-48 text-center">
                {seconds.toString().padStart(2, '0')}
              </span>
              <span className="text-lg text-gray-300 mt-4">{t('common:label.seconds')}</span>
            </div>
          </div>

          <div className="flex items-center justify-center space-x-6">
            {!isRunning ? (
              <Button 
                onClick={start} 
                size="lg" 
                className="px-8 py-4 text-xl" 
                disabled={!canStart}
              >
                <i className="ri-play-fill mr-3 text-xl"></i>
                {t('common:button.start')}
              </Button>
            ) : (
              <Button onClick={pause} variant="secondary" size="lg" className="px-8 py-4 text-xl">
                <i className="ri-pause-fill mr-3 text-xl"></i>
                {t('common:button.pause')}
              </Button>
            )}
            <Button onClick={reset} variant="danger" size="lg" className="px-8 py-4 text-xl">
              <i className="ri-refresh-line mr-3 text-xl"></i>
              {t('common:button.reset')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Normal view
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-4 md:py-8">
        {/* Back button */}
        <div className="mb-4 md:mb-6">
          <Button onClick={() => navigate('/timer')} variant="secondary" className="whitespace-nowrap">
            <i className="ri-arrow-left-line mr-2"></i>
            {t('common:button.back')}
          </Button>
        </div>

        {/* Main timer panel */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <div className="text-center">
            <div className="flex justify-between items-start mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                {isSecondsTimer
                  ? t('pageTitleSeconds', { seconds: timerSeconds })
                  : isHoursTimer
                  ? (timerHours === 1 ? t('pageTitleHour', { hours: timerHours }) : t('pageTitleHours', { hours: timerHours }))
                  : t('pageTitle', { minutes: timerMinutes })
                }
              </h2>

              <div className="flex items-center space-x-4">
                {/* Sound toggle */}
                <div className="flex items-center space-x-2">
                  <span className="hidden md:block text-sm text-gray-600">{t('common:label.soundAlert')}</span>
                  <span className="md:hidden text-sm text-gray-600">{t('common:label.sound')}</span>
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      soundEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        soundEnabled ? 'transform translate-x-7' : 'transform translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Fullscreen toggle */}
                <button
                  onClick={toggleFullscreen}
                  className="text-gray-600 hover:text-blue-600 transition-colors p-2 hover:bg-gray-100 rounded-lg flex items-center space-x-2"
                  title="Fullscreen"
                >
                  <span className="hidden md:block text-sm font-medium">{t('common:label.fullscreen')}</span>
                  <i className="ri-picture-in-picture-2-line text-xl"></i>
                </button>
              </div>
            </div>

            {/* Time display */}
            <div className="mb-8 md:mb-10">
              <div className="flex items-center justify-center space-x-1 md:space-x-2 mb-6 md:mb-8">
                {(hours > 0 || editingHours) && (
                  <>
                    <div className="flex flex-col items-center">
                      {editingHours ? (
                        <input
                          type="number"
                          value={tempHours}
                          onChange={e => handleHoursChange(e.target.value)}
                          onBlur={handleHoursSubmit}
                          onKeyDown={e => handleKeyPress(e, true)}
                          className="text-6xl md:text-8xl font-mono font-[700] text-blue-600 bg-transparent border-b-4 border-blue-300 text-center w-24 md:w-32 focus:outline-none focus:border-blue-500"
                          autoFocus
                          min="0"
                          max="23"
                        />
                      ) : (
                        <button
                          onClick={handleHoursClick}
                          className="text-6xl md:text-8xl font-mono font-[700] text-blue-600 hover:text-blue-700 cursor-pointer transition-colors w-24 md:w-32 text-center"
                          disabled={isRunning}
                        >
                          {hours.toString().padStart(2, '0')}
                        </button>
                      )}
                      <span className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2 font-medium">{t('common:label.hours')}</span>
                    </div>

                    <div className="flex items-center justify-center h-20 md:h-32">
                      <span className="text-6xl md:text-8xl font-mono font-[700] text-gray-400">:</span>
                    </div>
                  </>
                )}

                {/* Minutes */}
                <div className="flex flex-col items-center">
                  {editingMinutes ? (
                    <input
                      type="number"
                      value={tempMinutesValue}
                      onChange={e => handleMinutesChange(e.target.value)}
                      onBlur={handleMinutesSubmit}
                      onKeyDown={e => handleKeyPress(e, false)}
                      className="text-6xl md:text-8xl font-mono font-[700] text-blue-600 bg-transparent border-b-4 border-blue-300 text-center w-24 md:w-32 focus:outline-none focus:border-blue-500"
                      autoFocus
                      min="0"
                      max="59"
                    />
                  ) : (
                    <button
                      onClick={handleMinutesClick}
                      className="text-6xl md:text-8xl font-mono font-[700] text-blue-600 hover:text-blue-700 cursor-pointer transition-colors w-24 md:w-32 text-center"
                      disabled={isRunning}
                    >
                      {minutesDisplay.toString().padStart(2, '0')}
                    </button>
                  )}
                  <span className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2 font-medium">{t('common:label.minutes')}</span>
                </div>

                <div className="flex items-center justify-center h-20 md:h-32">
                  <span className="text-6xl md:text-8xl font-mono font-[700] text-gray-400">:</span>
                </div>

                {/* Seconds */}
                <div className="flex flex-col items-center">
                  <span className="text-6xl md:text-8xl font-mono font-[700] text-gray-600 w-24 md:w-32 text-center">
                    {seconds.toString().padStart(2, '0')}
                  </span>
                  <span className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2 font-medium">{t('common:label.seconds')}</span>
                </div>
              </div>

              {!isRunning && (
                <div className="text-center mb-4 md:mb-6">
                  <p className="text-gray-500 text-sm">{t('common:message.clickToEdit')}</p>
                  {hours === 0 && !editingHours && (
                    <button onClick={handleHoursClick} className="text-blue-600 hover:text-blue-700 text-sm underline mt-1">
{t('common:message.addHours')}
                    </button>
                  )}
                </div>
              )}

                {/* Quick add time buttons */}
                <div className="grid grid-cols-2 md:flex md:items-center md:justify-center gap-2 md:gap-3 mb-6 md:mb-8">
                    <Button onClick={() => addSeconds(30)} variant="secondary" size="sm" className="whitespace-nowrap">
                        {t('common:quick.add30s')}
                    </Button>
                    <Button onClick={() => addTime(1)} variant="secondary" size="sm" className="whitespace-nowrap">
                        {t('common:quick.add1min')}
                    </Button>
                    <Button onClick={() => addTime(5)} variant="secondary" size="sm" className="whitespace-nowrap">
                        {t('common:quick.add5min')}
                    </Button>
                    <Button onClick={() => addTime(10)} variant="secondary" size="sm" className="whitespace-nowrap">
                        {t('common:quick.add10min')}
                    </Button>
                    <Button onClick={() => addTime(30)} variant="secondary" size="sm" className="whitespace-nowrap">
                        {t('common:quick.add30min')}
                    </Button>
                </div>

                {/* Control buttons */}
              <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
                {!isRunning ? (
                  <Button 
                    onClick={start} 
                    size="md" 
                    className="px-4 py-3 md:px-6 md:py-4 w-full whitespace-nowrap" 
                    disabled={!canStart}
                  >
                    <i className="ri-play-fill mr-2"></i>
                    {t('common:button.start')}
                  </Button>
                ) : (
                  <Button onClick={pause} variant="secondary" size="md" className="px-4 py-3 md:px-6 md:py-4 w-full whitespace-nowrap">
                    <i className="ri-pause-fill mr-2"></i>
                    {t('common:button.pause')}
                  </Button>
                )}
                <Button onClick={reset} variant="danger" size="md" className="px-4 py-3 md:px-6 md:py-4 w-full whitespace-nowrap">
                  <i className="ri-refresh-line mr-2"></i>
                  {t('common:button.reset')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Use cases - 只在有内容时显示 */}
        {hasUseCases && (
          <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
            <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">
              {isSecondsTimer
                ? t('useCases.titleSeconds', { seconds: timerSeconds })
                : isHoursTimer
                ? t('useCases.titleHours', { hours: timerHours })
                : t('useCases.title', { minutes: timerMinutes })
              }
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {timerMinutes === 1 && (
              <>
                <div className="flex items-start space-x-3">
                  <i className="ri-lightbulb-line text-yellow-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.quickBreak.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.quickBreak.desc')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-eye-line text-blue-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.eyeCare.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.eyeCare.desc')}</p>
                  </div>
                </div>
              </>
            )}
            {timerMinutes === 3 && (
              <>
                <div className="flex items-start space-x-3">
                  <i className="ri-cup-line text-brown-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.teaBrewing.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.teaBrewing.desc')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-run-line text-green-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.lightExercise.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.lightExercise.desc')}</p>
                  </div>
                </div>
              </>
            )}
            {timerMinutes === 5 && (
              <>
                <div className="flex items-start space-x-3">
                  <i className="ri-heart-pulse-line text-red-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.meditation.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.meditation.desc')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-bowl-line text-orange-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.softBoiledEggs.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.softBoiledEggs.desc')}</p>
                  </div>
                </div>
              </>
            )}
            {timerMinutes === 10 && (
              <>
                <div className="flex items-start space-x-3">
                  <i className="ri-book-open-line text-blue-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.quickReading.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.quickReading.desc')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-phone-line text-green-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.phoneCall.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.phoneCall.desc')}</p>
                  </div>
                </div>
              </>
            )}
            {timerMinutes === 15 && (
              <>
                <div className="flex items-start space-x-3">
                  <i className="ri-focus-2-line text-purple-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.focusedWork.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.focusedWork.desc')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-group-line text-blue-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.shortMeeting.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.shortMeeting.desc')}</p>
                  </div>
                </div>
              </>
            )}
            {timerMinutes === 30 && (
              <>
                <div className="flex items-start space-x-3">
                  <i className="ri-focus-3-line text-indigo-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.deepWork.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.deepWork.desc')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-run-fill text-orange-500 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('useCases.workout.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('useCases.workout.desc')}</p>
                  </div>
                </div>
              </>
            )}
            </div>
          </div>
        )}

        {/* Popular timers */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('popular.title')}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-3">
            {(() => {
              if (isSecondsTimer) {
                // 秒级timer：显示当前秒数-10到+10的范围
                const start = Math.max(1, timerSeconds - 10);
                const end = timerSeconds + 10;
                const timers = [];
                for (let i = start; i <= end; i++) {
                  timers.push({
                    label: `${i} Seconds Timer`,
                    value: i,
                    path: `/timer/${i}-seconds-timer`
                  });
                }
                return timers;
              } else if (isHoursTimer) {
                // 小时级timer：显示当前小时数-10到+10的范围
                const start = Math.max(1, timerHours - 10);
                const end = timerHours + 10;
                const timers = [];
                for (let i = start; i <= end; i++) {
                  timers.push({
                    label: `${i} ${i === 1 ? 'Hour' : 'Hours'} Timer`,
                    value: i,
                    path: `/timer/${i}-hour-timer`
                  });
                }
                return timers;
              } else {
                // 分钟级timer：显示当前分钟数-10到+10的范围
                const start = Math.max(1, timerMinutes - 10);
                const end = timerMinutes + 10;
                const timers = [];
                for (let i = start; i <= end; i++) {
                  timers.push({
                    label: `${i} Minute Timer`,
                    value: i,
                    path: `/timer/${i}-minute-timer`
                  });
                }
                return timers;
              }
            })().map(timer => (
              <button
                key={timer.value}
                onClick={() => navigate(timer.path)}
                className="text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer text-sm md:text-base text-left"
              >
                {timer.label}
              </button>
            ))}
          </div>
        </div>

        {/* How to use */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">
            {isSecondsTimer
              ? t('howto.titleSeconds', { seconds: timerSeconds })
              : isHoursTimer
              ? (timerHours === 1 ? t('howto.titleHour', { hours: timerHours }) : t('howto.titleHours', { hours: timerHours }))
              : t('howto.titleMinutes', { minutes: timerMinutes })
            }
          </h3>
          <div className="space-y-3 md:space-y-4 text-gray-600">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">1</div>
              <p className="text-sm md:text-base">{t('howto.step1')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">2</div>
              <p className="text-sm md:text-base">{t('howto.step2')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">3</div>
              <p className="text-sm md:text-base">{t('howto.step3')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">4</div>
              <p className="text-sm md:text-base">{t('howto.step4')}</p>
            </div>
          </div>
        </div>

        {/* User Reviews */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('reviews.title')}</h3>
          <div className="relative overflow-hidden">
            <div className="flex overflow-x-auto space-x-4 md:space-x-8 scrollbar-hide">
              {randomizedReviews.map(review => (
                <div key={review.id} className="flex-shrink-0 bg-gray-50 p-4 md:p-6 rounded-lg w-80">
                  <div className="flex items-center mb-3 md:mb-4">
                    <img
                      src={review.avatar}
                      alt={`${review.name} avatar`}
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex text-yellow-400 mb-1">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`${i < review.rating ? 'ri-star-fill' : 'ri-star-line'} text-sm`}></i>
                        ))}
                      </div>
                      <span className="text-xs md:text-sm text-gray-600 font-medium">{review.name}</span>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">{review.review}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-100 text-gray-800 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col lg:flex-row justify-between items-start space-y-6 lg:space-y-0">
            {/* Left side - Website description */}
            <div className="lg:w-2/3">
              <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('common:footer.about')}</h4>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {t('common:footer.aboutDesc')}
              </p>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <i className="ri-timer-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('page.title')}</span> - {t('common:footer.timerDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-play-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.stopwatch')}</span> - {t('common:footer.stopwatchDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-global-line text-purple-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.worldclock')}</span> - {t('common:footer.worldClockDesc')}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <a href="/terms" className="hover:text-gray-700 transition-colors">{t('common:footer.termsOfService')}</a>
                  <span>|</span>
                  <a href="#" className="hover:text-gray-700 transition-colors">{t('common:footer.privacy')}</a>
                  <span>|</span>
                  <a href="mailto:mu9347mu@gmail.com?subject=Contact Us - timer.today&body=Hello timer.today team,%0D%0A%0D%0AI would like to..." className="hover:text-gray-700 transition-colors">{t('common:footer.contactUs')}</a>
                  <span>|</span>
                  <span>{t('common:footer.copyright')}</span>
                  <span className="lg:hidden">|</span>
                  <div className="lg:hidden">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </div>

            {/* Vertical divider - only visible on large screens */}
            <div className="hidden lg:flex w-px bg-gray-300 mx-6 h-40 self-center"></div>

            {/* Right side - Multilingual content - only show on desktop */}
            <div className="hidden lg:block lg:w-1/3">
              <div className="space-y-3 text-sm text-gray-600 mt-12">
                <p>timer countdown pomodoro focus work break</p>
                <p>计时器 倒计时 番茄钟 专注 休息</p>
                <p>タイマー カウントダウン ポモドーロ 集中 休憩</p>
                <p>temporizador cuenta regresiva pomodoro concentración</p>
                <p>Timer Countdown Pomodoro Konzentration Pause</p>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Timer Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('common:message.timerComplete')}</h3>
              <p className="text-gray-600">{t('common:message.timerCompleteBody')}</p>
            </div>
            <Button onClick={handleCompletionModalClose} className="w-full">
              {t('common:button.ok')}
            </Button>
          </div>
        </div>
      )}

    </div>
  );
}
